import moment from 'moment';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './calendar-grid.module.scss';
import { EventChip } from './event-chip';
import { buildTimeLayout, laneWidthPx } from './layout/time-layout';
import { planChipContent } from './layout/chip-content';
import { occurrenceKey } from './layout/occurrence-key';
import { snapToSlot } from './layout/drag-model';
import type { DragState } from './use-calendar-drag';
import type { CalendarLeanEvent } from './types';

export const PX_PER_HOUR = 88;
export const BLOCK_HEIGHT_PX = 40;
export const SLOT_MINUTES = 15;

interface Props {
    days: Date[];
    events: CalendarLeanEvent[];
    now: number;
    drag: DragState | null;
    /** The slot under the pointer would refuse this drop. */
    dropRefused?: boolean;
    gridRef: React.RefObject<HTMLDivElement>;
    beginDrag: (event: CalendarLeanEvent, e: React.PointerEvent<HTMLElement>) => void;
    onOpenEvent: (event: CalendarLeanEvent, mouse: { x: number; y: number }) => void;
    onCreateAt: (at: Date) => void;
    onHoverStart?: (event: CalendarLeanEvent, rect: DOMRect) => void;
    onHoverEnd?: () => void;
    /** Delete the ONE occurrence a chip stands for. */
    onDelete?: (event: CalendarLeanEvent) => void;
}

export const TimeGrid: React.FC<Props> = ({
    days, events, now, drag, dropRefused, gridRef, beginDrag, onOpenEvent, onCreateAt,
    onHoverStart, onHoverEnd, onDelete,
}) => {
    const positioned = useMemo(
        () => buildTimeLayout(events, {
            days,
            dayStartHour: 0,
            dayEndHour: 24,
            pxPerHour: PX_PER_HOUR,
            blockHeightPx: BLOCK_HEIGHT_PX,
        }),
        [events, days],
    );

    // Where a release would land. Month shows this as a highlighted cell; a time
    // grid has no cell to highlight, so without this the user is holding an
    // event over an empty ruled surface with no idea what a release will do.
    const dropIndicator = useMemo(() => {
        if (!drag?.active || drag.targetMs == null) return null;
        const at = moment(drag.targetMs);
        const dayIndex = days.findIndex(day => moment(day).isSame(at, 'day'));
        if (dayIndex === -1) return null;
        return {
            dayIndex,
            topPx: (at.hour() + at.minute() / 60) * PX_PER_HOUR,
            label: at.format('HH:mm'),
        };
    }, [drag?.active, drag?.targetMs, days]);

    // Hovering empty time should read the same way dragging onto it does: the
    // user is told, in place, that a click here creates a send at this time.
    // Without it the grid looks inert and the click is a guess.
    const [hoverSlot, setHoverSlot] = useState<{ dayIndex: number; topPx: number; label: string } | null>(null);

    const trackHover = useCallback((e: React.PointerEvent<HTMLDivElement>, dayIndex: number, day: Date) => {
        if (drag?.active) return;                       // the drag indicator owns the surface
        if (!(e.target as HTMLElement).classList.contains(styles.slot)) { setHoverSlot(null); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const hours = (e.clientY - rect.top) / PX_PER_HOUR;
        if (hours < 0 || hours >= 24) { setHoverSlot(null); return; }
        const at = snapToSlot(moment(day).startOf('day').add(hours * 60, 'minutes').valueOf(), SLOT_MINUTES);
        const atMoment = moment(at);
        const topPx = (atMoment.hour() + atMoment.minute() / 60) * PX_PER_HOUR;
        // A pointer crosses dozens of pixels inside one 15-minute slot. Handing
        // React a new object for the SAME slot re-rendered the whole week on
        // every one of them; keeping the previous object lets it bail out.
        setHoverSlot(prev => (prev && prev.dayIndex === dayIndex && prev.topPx === topPx
            ? prev
            : { dayIndex, topPx, label: atMoment.format('HH:mm') }));
    }, [drag?.active]);

    // The 48 ruled half-hours of a column never change. Built once and reused by
    // every column, so a grid re-render (a drag or hover moves it on every
    // pointer event) does not reconcile 48 × 7 identical divs each time: React
    // skips an element it is handed back unchanged.
    const slotCells = useMemo(
        () => Array.from({ length: 48 }, (_, slot) => <div key={slot} className={styles.slot} />),
        [],
    );

    // Week and day opened at 00:00 — a wall of empty night, with the day's real
    // content below the fold and nothing telling the user to scroll. Open where
    // the day actually is: the first event, or now, whichever is earlier.
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const scrollKey = `${days[0]?.toDateString() ?? ''}|${days.length}`;
    useEffect(() => {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const firstEventTop = positioned.length
            ? Math.min(...positioned.map(p => p.topPx))
            : Number.POSITIVE_INFINITY;
        const nowTop = (moment(now).hour() + moment(now).minute() / 60) * PX_PER_HOUR;
        const anchor = Math.min(firstEventTop, nowTop);
        const target = Number.isFinite(anchor) ? anchor : 8 * PX_PER_HOUR;
        scroller.scrollTop = Math.max(0, target - 120);
        // Only when the rendered days change: re-running on every `now` tick
        // would yank the view back while the user is reading it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollKey]);

    // A chip's share of its column is pure layout (1 / laneCount, from
    // buildTimeLayout); the column's own pixel width is not — the tracks are
    // `1fr` inside a fluid scroller, so it only exists after layout. One read of
    // the whole grid, refreshed on window resize, is enough to turn the share
    // into the width `planChipContent` needs: no per-chip observer, and the
    // thresholds still live in the pure function rather than in this JSX.
    const [columnWidthPx, setColumnWidthPx] = useState(0);
    useLayoutEffect(() => {
        const measure = () => {
            const width = gridRef.current?.clientWidth ?? 0;
            setColumnWidthPx(width / Math.max(1, days.length));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [days.length, gridRef]);

    const nowMoment = moment(now);
    const nowTopPx = (nowMoment.hour() + nowMoment.minute() / 60) * PX_PER_HOUR;
    const draggingKey = drag?.active ? drag.key : null;

    return (
        // The day header lives INSIDE the scroller, as a sticky row of the same
        // grid as the columns. As siblings they were two grids over two widths —
        // the body's is narrower by the scrollbar — so every day separator in the
        // header sat a couple of pixels off the one below it, and the offset grew
        // across the week. Same grid, same tracks, lines that actually meet.
        <div className={styles.timeWrap} ref={scrollerRef}>
            <div className={styles.timeHead}>
                <div className={styles.timeHeadSpacer} />
                <div className={styles.timeHeadDays} style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
                    {days.map(day => {
                        const isToday = moment(day).isSame(nowMoment, 'day');
                        return (
                            <div
                                key={day.toISOString()}
                                className={[styles.timeHeadDay, isToday ? styles.timeHeadDayToday : ''].filter(Boolean).join(' ')}
                            >
                                <div className={styles.timeHeadDow}>{moment(day).format('ddd')}</div>
                                <div className={[styles.timeHeadNum, isToday ? styles.timeHeadNumToday : ''].filter(Boolean).join(' ')}>
                                    {day.getDate()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className={styles.gutter}>
                    {Array.from({ length: 24 }, (_, hour) => (
                        <div key={hour} className={styles.gutterHour}>{String(hour).padStart(2, '0')}:00</div>
                    ))}
                </div>

                <div
                    className={styles.columns}
                    ref={gridRef}
                    style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}
                >
                    {days.map((day, dayIndex) => {
                        const isToday = moment(day).isSame(nowMoment, 'day');
                        return (
                            <div
                                key={day.toISOString()}
                                className={[
                                    styles.column,
                                    isToday ? styles.columnToday : '',
                                    dropIndicator?.dayIndex === dayIndex ? styles.columnDropTarget : '',
                                ].filter(Boolean).join(' ')}
                                onPointerMove={e => trackHover(e, dayIndex, day)}
                                onPointerLeave={() => setHoverSlot(null)}
                                onClick={e => {
                                    // Only an empty slot creates: a click that landed on a chip
                                    // has already been handled by the chip itself.
                                    if (!(e.target as HTMLElement).classList.contains(styles.slot)) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const hours = (e.clientY - rect.top + e.currentTarget.scrollTop) / PX_PER_HOUR;
                                    const at = moment(day).startOf('day').add(hours * 60, 'minutes').valueOf();
                                    onCreateAt(new Date(snapToSlot(at, SLOT_MINUTES)));
                                }}
                            >
                                {slotCells}

                                {positioned.filter(p => p.dayIndex === dayIndex).map(p => {
                                    // Before the first measurement there is no width to reason
                                    // about, so the chip renders whole rather than flashing a
                                    // stripped-down version for one frame.
                                    const content = columnWidthPx
                                        ? planChipContent({
                                            widthPx: laneWidthPx(columnWidthPx, p.laneCount),
                                            hasImage: !!p.event.asset?.url,
                                        })
                                        : undefined;
                                    return (
                                    <div
                                        key={occurrenceKey(p.event)}
                                        className={styles.positioned}
                                        style={{
                                            top: p.topPx,
                                            height: p.heightPx,
                                            insetInlineStart: `calc(${(p.lane / p.laneCount) * 100}% + 4px)`,
                                            width: `calc(${100 / p.laneCount}% - 8px)`,
                                        }}
                                    >
                                        <EventChip
                                            event={p.event}
                                            now={now}
                                            size="time"
                                            content={content}
                                            isDragging={draggingKey === occurrenceKey(p.event)}
                                            onOpen={onOpenEvent}
                                            onDragStart={beginDrag}
                                            onHoverStart={onHoverStart}
                                            onHoverEnd={onHoverEnd}
                                            onDelete={onDelete}
                                        />
                                    </div>
                                    );
                                })}

                                {!drag?.active && hoverSlot?.dayIndex === dayIndex && (
                                    <div
                                        className={styles.hoverSlot}
                                        style={{ top: hoverSlot.topPx, height: BLOCK_HEIGHT_PX }}
                                    >
                                        <span>+ {hoverSlot.label}</span>
                                    </div>
                                )}

                                {dropIndicator?.dayIndex === dayIndex && (
                                    <div
                                        className={[
                                            styles.dropSlot,
                                            dropRefused ? styles.dropSlotRefused : '',
                                        ].filter(Boolean).join(' ')}
                                        style={{ top: dropIndicator.topPx, height: BLOCK_HEIGHT_PX }}
                                    >
                                        {/* A refused slot must not show a landing time: the
                                            time is what the grid is declining to give. */}
                                        <span>{dropRefused ? '✕' : dropIndicator.label}</span>
                                    </div>
                                )}

                                {isToday && <div className={styles.nowLine} style={{ top: nowTopPx }} />}
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};
